import { onContentReady, onContentReplacing } from './contentLifecycle.js';
import { attachVueErrorHandler } from './sentry.js';

// Mounting Vue islands so they survive a content swap.
//
// Every Vue root on a content page (the Last Minute listing, the Full Product Category
// listing, the standalone spa switcher) used to be mounted by its own Vite entry doing
// `document.querySelectorAll(sel).forEach(createApp...)` once, at module evaluation. That
// is exactly the pattern the content swap breaks, and it breaks in three separate ways:
//
//   1. A module URL already evaluated is never evaluated again, so an entry that ran on
//      the first page never mounts into the swapped markup.
//   2. A <script type="module"> inserted via innerHTML never executes at all, so a section
//      appearing for the first time in swapped content would not mount either - which is
//      why the swapper appends new scripts to the document itself rather than relying on
//      the markup carrying them.
//   3. Outgoing apps were never unmounted, so their watchers, timers and event listeners
//      would leak one set per swap.
//
// registerIsland() answers all three, and is the ONLY way a Vue root should be mounted
// into page content. The search bar, mode toggle, basket button and panel host are all
// mounted directly by their own entries instead, and correctly so: they live in the header
// or at body level, are never inside a swapped region, and must NOT be torn down when the
// content changes - keeping the search bar alive through a swap is the point of the whole
// feature.

// Which element is running which app, so an element already mounted is never mounted twice
// and an outgoing element's app can be found and unmounted. Weak, so a detached element
// and its app become collectable together once the swap drops the last reference.
const mounted = new WeakMap();

/**
 * Mount a Vue app into every element matching `selector`, in the initial page and in every
 * region of swapped content, and unmount it again when its markup is replaced.
 *
 * @param {string} selector Attribute selector for the island's mount points.
 * @param {(el: Element) => import('vue').App} create Builds the app for one element. Called
 *        once per element; `createApp(Component, props)` with props read from the element's
 *        dataset, and no mount() call - this does that.
 */
export function registerIsland(selector, create) {
    onContentReady((root) => {
        // A root may itself be a mount point when a region's own element carries the
        // attribute, which querySelectorAll would not return.
        const elements = [
            ...(root.matches?.(selector) ? [root] : []),
            ...root.querySelectorAll(selector),
        ];

        elements.forEach((el) => {
            if (mounted.has(el)) {
                return;
            }

            const app = create(el);

            // Registered here rather than in each entry, so an island cannot be added
            // without its errors reaching Sentry. Vue swallows component errors into its
            // own console logger and never lets them reach window.onerror, so a single
            // global Sentry.init() does not cover them - see sentry.js.
            attachVueErrorHandler(app);

            // Track BEFORE mounting: a component that throws during setup must still be
            // recorded, or the next sweep would mount a second copy over the wreckage of
            // the first.
            mounted.set(el, app);

            app.mount(el);
        });
    });

    onContentReplacing((root) => {
        // Vue's mount(el) replaces the element's children and leaves the element itself in
        // place, so the mount points are still findable by the same selector here. This
        // runs while the outgoing markup is still in the document, which is the only
        // moment they can be found at all.
        const elements = [
            ...(root.matches?.(selector) ? [root] : []),
            ...root.querySelectorAll(selector),
        ];

        elements.forEach((el) => {
            const app = mounted.get(el);

            if (! app) {
                return;
            }

            // Unmounting runs the components' own teardown - watchers stopped, listeners
            // removed, onBeforeUnmount hooks called - which discarding the markup alone
            // would not.
            app.unmount();
            mounted.delete(el);
        });
    });
}
